import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import BarcodePrintModal from '../components/BarcodePrintModal'
import TelegramButton from '../components/TelegramButton'
import { SyncModal } from '../components/SyncModal'
import { useAuthStore } from '../stores/authStore'
import { API_CONFIG } from '../lib/config'
import { toast } from 'sonner'
import {
    Search,
    Filter,
    Package,
    TrendingUp,
    Database,
    RefreshCw,
    Printer,
    Eye,
    BarChart3,
    Grid3X3,
    List,
    ChevronDown,
    ChevronUp
} from 'lucide-react'
import type { Product, ProductsResponse } from '../types/product'

export default function ProductsPage() {
    const [currentPage, setCurrentPage] = useState(1)
    const [pageSize, setPageSize] = useState(20)
    const [searchQuery, setSearchQuery] = useState('')
    const [stockFilter, setStockFilter] = useState<'all' | 'zero' | 'lt5' | 'gt5'>('all')
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [viewMode, setViewMode] = useState<'table' | 'grid'>('table')
    const [selectedImage, setSelectedImage] = useState<string | null>(null)
    const [isImageModalOpen, setIsImageModalOpen] = useState(false)
    const [isXmlSyncing, setIsXmlSyncing] = useState(false)
    const [sortBy, setSortBy] = useState<'name' | 'sku' | 'stock' | 'price' | 'lastUpdated' | 'dateAdd'>('dateAdd')
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
    const [expandedProducts, setExpandedProducts] = useState<Set<string>>(new Set())
    const [isSyncModalOpen, setIsSyncModalOpen] = useState(false)

    const { accessToken } = useAuthStore()

    const { data, isLoading, error, refetch } = useQuery<ProductsResponse>({
        queryKey: ['products', currentPage, pageSize, searchQuery, stockFilter, sortBy, sortOrder],
        queryFn: async () => {
            const params = new URLSearchParams({
                page: currentPage.toString(),
                size: pageSize.toString(),
                ...(searchQuery && { query: searchQuery }),
                ...(stockFilter && { stock: stockFilter }),
                sortBy: sortBy,
                sortOrder: sortOrder
            })

            const response = await fetch(`${API_CONFIG.BASE_URL}/api/products?${params}`, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            })

            if (!response.ok) {
                throw new Error('Ürünler yüklenemedi')
            }

            return response.json()
        },
        enabled: !!accessToken
    })

    // Toplam sayılar için hafif sorgular (yalnızca toplam değerlerini okur)
    const { data: totalAll } = useQuery<{ pagination: { total: number } }>({
        queryKey: ['products-total-all', searchQuery],
        queryFn: async () => {
            const params = new URLSearchParams({ page: '1', size: '1', ...(searchQuery && { query: searchQuery }) })
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/products?${params}`, { headers: { 'Authorization': `Bearer ${accessToken}` } })
            return res.json()
        },
        enabled: !!accessToken
    })
    const { data: totalZero } = useQuery<{ pagination: { total: number } }>({
        queryKey: ['products-total-zero', searchQuery],
        queryFn: async () => {
            const params = new URLSearchParams({ page: '1', size: '1', stock: 'zero', ...(searchQuery && { query: searchQuery }) })
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/products?${params}`, { headers: { 'Authorization': `Bearer ${accessToken}` } })
            return res.json()
        },
        enabled: !!accessToken
    })
    const { data: totalLt5 } = useQuery<{ pagination: { total: number } }>({
        queryKey: ['products-total-lt5', searchQuery],
        queryFn: async () => {
            const params = new URLSearchParams({ page: '1', size: '1', stock: 'lt5', ...(searchQuery && { query: searchQuery }) })
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/products?${params}`, { headers: { 'Authorization': `Bearer ${accessToken}` } })
            return res.json()
        },
        enabled: !!accessToken
    })

    const totalCount = totalAll?.pagination?.total || 0
    const outOfStockCount = totalZero?.pagination?.total || 0
    const lowStockCount = totalLt5?.pagination?.total || 0
    const activeCount = Math.max(totalCount - outOfStockCount, 0)

    // Ürün açıklamasından ilk görsel URL'sini çek
    const extractImageUrl = (text?: string) => {
        if (!text) return null
        const match = text.match(/https?:[^\s"']+\.(?:png|jpg|jpeg|gif)/i)
        return match ? match[0] : null
    }

    // XML Senkronizasyon fonksiyonu
    const handleXmlSync = async () => {
        if (!accessToken) return

        setIsXmlSyncing(true)
        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}/api/products/sync-xml`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                }
            })

            if (!response.ok) {
                throw new Error('XML senkronizasyon hatası')
            }

            const result = await response.json()
            console.log('XML Sync sonucu:', result)

            // Başarılı senkronizasyon sonrası verileri yenile
            await refetch()

            // Başarı mesajı göster
            toast.success(`XML senkronizasyon tamamlandı! ${result.total} ürün işlendi.`)

        } catch (error) {
            console.error('XML Sync hatası:', error)
            const errorMessage = error instanceof Error ? error.message : 'Bilinmeyen hata'
            toast.error('XML senkronizasyon hatası: ' + errorMessage)
        } finally {
            setIsXmlSyncing(false)
        }
    }

    // Resim modal'ını aç
    const handleImageClick = (imageUrl: string) => {
        setSelectedImage(imageUrl)
        setIsImageModalOpen(true)
    }

    const toggleProductExpansion = (productId: string) => {
        setExpandedProducts(prev => {
            const newSet = new Set(prev)
            if (newSet.has(productId)) {
                newSet.delete(productId)
            } else {
                newSet.add(productId)
            }
            return newSet
        })
    }

    const handlePrintBarcode = (product: Product) => {
        setSelectedProduct(product)
        setIsModalOpen(true)
    }

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault()
        setCurrentPage(1)
        refetch()
    }

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage)
    }

    const handlePageSizeChange = (newSize: number) => {
        setPageSize(newSize)
        setCurrentPage(1)
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
                <Card className="max-w-2xl mx-auto shadow-2xl border-0 bg-white/80 backdrop-blur-sm">
                    <CardHeader className="text-center">
                        <CardTitle className="text-red-600 text-2xl">Hata</CardTitle>
                        <CardDescription className="text-lg">
                            Ürünler yüklenirken bir hata oluştu: {error.message}
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="text-center">
                        <Button onClick={() => refetch()} className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
                            <RefreshCw className="w-5 h-5 mr-2" />
                            Tekrar Dene
                        </Button>
                    </CardContent>
                </Card>
            </div>
        )
    }

    return (
        <div className="min-h-screen w-full bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
            {/* Header */}
            <div className="bg-white/80 backdrop-blur-xl shadow-lg border-b border-white/20 mb-8">
                <div className="w-full px-4 sm:px-8 lg:px-20 py-4 sm:py-6">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        <div>
                            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-slate-900 via-blue-900 to-indigo-900 bg-clip-text text-transparent">
                                Ürün Yönetimi
                            </h1>
                            <p className="text-slate-600 mt-2 text-sm sm:text-base lg:text-lg">Stok takibi ve ürün bilgileri</p>
                        </div>
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 w-full sm:w-auto">
                                <div className="flex items-center space-x-2 sm:space-x-3 text-xs sm:text-sm text-slate-700 bg-white/70 px-3 sm:px-4 py-2 rounded-xl">
                                    <Database className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                                    <span className="font-semibold">Toplam: {totalCount}</span>
                                </div>
                                <div className="flex items-center space-x-2 sm:space-x-3 text-xs sm:text-sm text-slate-700 bg-white/70 px-3 sm:px-4 py-2 rounded-xl">
                                    <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                                    <span className="font-semibold">Aktif: {activeCount}</span>
                                </div>
                                <div className="flex items-center space-x-2 sm:space-x-3 text-xs sm:text-sm text-slate-700 bg-white/70 px-3 sm:px-4 py-2 rounded-xl">
                                    <Package className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" />
                                    <span className="font-semibold">Stok {"<"} 5: {lowStockCount}</span>
                                </div>
                            </div>
                            <div className="flex gap-2 sm:gap-3">
                                <Button onClick={() => refetch()} variant="outline" size="sm" className="bg-white/60 hover:bg-white/80 border-white/20 text-xs sm:text-sm">
                                    <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                                    <span className="hidden sm:inline">Yenile</span>
                                </Button>
                                <Button
                                    onClick={() => setIsSyncModalOpen(true)}
                                    variant="outline"
                                    size="sm"
                                    className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white border-0 shadow-lg text-xs sm:text-sm font-bold"
                                >
                                    <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                                    <span className="hidden sm:inline">TAM SENKRONİZASYON</span>
                                    <span className="sm:hidden">TAM SYNC</span>
                                </Button>
                                <Button
                                    onClick={handleXmlSync}
                                    variant="outline"
                                    size="sm"
                                    className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white border-0 shadow-lg text-xs sm:text-sm"
                                    disabled={isXmlSyncing}
                                >
                                    {isXmlSyncing ? (
                                        <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 animate-spin" />
                                    ) : (
                                        <Database className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                                    )}
                                    <span className="hidden sm:inline">{isXmlSyncing ? 'Senkronize Ediliyor...' : 'XML Senkronizasyon'}</span>
                                    <span className="sm:hidden">{isXmlSyncing ? 'Sync...' : 'XML'}</span>
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="w-full px-4 sm:px-8 lg:px-20 py-4 sm:py-6">
                {/* Filtreler ve Arama */}
                <Card className="mb-6 sm:mb-8 shadow-xl border-0 bg-white/80 backdrop-blur-sm">
                    <CardHeader className="pb-3 sm:pb-4">
                        <CardTitle className="flex items-center text-lg sm:text-xl">
                            <Filter className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3 text-blue-600" />
                            Filtreler ve Arama
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="px-4 sm:px-6 py-3 sm:py-4">
                        <form onSubmit={handleSearch} className="space-y-4">
                            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                                <div className="flex-1">
                                    <Label htmlFor="search" className="sr-only">Ürün Ara</Label>
                                    <div className="relative group">
                                        <Search className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-slate-400 group-focus-within:text-blue-600 transition-colors" />
                                        <Input
                                            id="search"
                                            type="text"
                                            placeholder="SKU, ürün adı veya açıklama ara..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            className="pl-10 sm:pl-12 h-10 sm:h-12 bg-white/60 border-slate-200 focus:bg-white focus:border-blue-500 focus:ring-blue-500 transition-all duration-300 rounded-xl text-sm"
                                        />
                                    </div>
                                </div>
                                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                                    <select
                                        value={stockFilter}
                                        onChange={(e) => { setStockFilter(e.target.value as any); setCurrentPage(1) }}
                                        className="h-10 sm:h-12 px-3 rounded-xl border-slate-200 bg-white text-sm"
                                    >
                                        <option value="all">Tümü</option>
                                        <option value="zero">Stok 0</option>
                                        <option value="lt5">Stok {"<"} 5</option>
                                        <option value="gt5">Stok {">"} 5</option>
                                    </select>
                                    <select
                                        value={sortBy}
                                        onChange={(e) => { setSortBy(e.target.value as any); setCurrentPage(1) }}
                                        className="h-10 sm:h-12 px-3 rounded-xl border-slate-200 bg-white text-sm"
                                    >
                                        <option value="dateAdd">Eklenme Tarihi</option>
                                        <option value="name">İsim</option>
                                        <option value="sku">SKU</option>
                                        <option value="stock">Stok</option>
                                        <option value="price">Fiyat</option>
                                        <option value="lastUpdated">Son Güncelleme</option>
                                    </select>
                                    <select
                                        value={sortOrder}
                                        onChange={(e) => { setSortOrder(e.target.value as any); setCurrentPage(1) }}
                                        className="h-10 sm:h-12 px-3 rounded-xl border-slate-200 bg-white text-sm"
                                    >
                                        <option value="desc">Yeni → Eski</option>
                                        <option value="asc">Eski → Yeni</option>
                                    </select>
                                </div>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                                <Button type="submit" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 h-10 sm:h-12 px-4 sm:px-6 rounded-xl shadow-lg text-sm">
                                    <Search className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                                    Ara
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => {
                                        setSearchQuery('')
                                        setCurrentPage(1)
                                    }}
                                    className="h-10 sm:h-12 px-4 sm:px-6 rounded-xl border-slate-200 hover:bg-slate-50 text-sm"
                                >
                                    Temizle
                                </Button>
                            </div>
                        </form>
                    </CardContent>
                </Card>

                {/* İstatistikler */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <Card className="shadow-xl border-0 bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                        <CardContent className="p-6">
                            <div className="flex items-center">
                                <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                                    <Package className="w-8 h-8 text-white" />
                                </div>
                                <div className="ml-4">
                                    <p className="text-blue-100 text-sm font-medium">Toplam Ürün</p>
                                    <p className="text-3xl font-bold">{totalCount}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="shadow-xl border-0 bg-gradient-to-br from-green-500 to-green-600 text-white">
                        <CardContent className="p-6">
                            <div className="flex items-center">
                                <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                                    <TrendingUp className="w-8 h-8 text-white" />
                                </div>
                                <div className="ml-4">
                                    <p className="text-green-100 text-sm font-medium">Aktif Ürün</p>
                                    <p className="text-3xl font-bold">{activeCount}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="shadow-xl border-0 bg-gradient-to-br from-purple-500 to-purple-600 text-white">
                        <CardContent className="p-6">
                            <div className="flex items-center">
                                <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                                    <BarChart3 className="w-8 h-8 text-white" />
                                </div>
                                <div className="ml-4">
                                    <p className="text-purple-100 text-sm font-medium">Stok 0</p>
                                    <p className="text-3xl font-bold">{outOfStockCount}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Ürün Tablosu */}
                <Card className="shadow-2xl border-0 bg-white/80 backdrop-blur-sm">
                    <CardHeader className="px-6 py-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <CardTitle className="text-2xl">Ürün Listesi</CardTitle>
                                <CardDescription className="text-lg">
                                    {data?.pagination?.total ? `${((currentPage - 1) * pageSize) + 1} - ${Math.min(currentPage * pageSize, data.pagination.total)} arası, toplam ${data.pagination.total} sonuçtan` : 'Ürün bulunamadı'}
                                </CardDescription>
                            </div>
                            <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                                {/* XML Sync Button */}
                                <Button
                                    onClick={handleXmlSync}
                                    variant="outline"
                                    size="sm"
                                    className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white border-0 shadow-lg"
                                    disabled={isXmlSyncing}
                                >
                                    {isXmlSyncing ? (
                                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                    ) : (
                                        <Database className="w-4 h-4 mr-2" />
                                    )}
                                    {isXmlSyncing ? 'Senkronize...' : 'XML Sync'}
                                </Button>

                                {/* View Mode Toggle */}
                                <div className="flex items-center bg-slate-100 rounded-lg p-1">
                                    <button
                                        onClick={() => setViewMode('table')}
                                        className={`p-2 rounded-md transition-all duration-200 ${viewMode === 'table' ? 'bg-white shadow-md text-blue-600' : 'text-slate-600 hover:text-slate-800'
                                            }`}
                                    >
                                        <List className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => setViewMode('grid')}
                                        className={`p-2 rounded-md transition-all duration-200 ${viewMode === 'grid' ? 'bg-white shadow-md text-blue-600' : 'text-slate-600 hover:text-slate-800'
                                            }`}
                                    >
                                        <Grid3X3 className="w-4 h-4" />
                                    </button>
                                </div>

                                <div className="flex items-center space-x-2">
                                    <Label htmlFor="pageSize" className="text-sm font-medium text-slate-700">Sayfa başına:</Label>
                                    <select
                                        id="pageSize"
                                        value={pageSize}
                                        onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                                        className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:border-blue-500 focus:ring-blue-500"
                                    >
                                        <option value={10}>10</option>
                                        <option value={20}>20</option>
                                        <option value={50}>50</option>
                                        <option value={100}>100</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="px-4 py-3">
                        {isLoading ? (
                            <div className="flex items-center justify-center py-16">
                                <div className="text-center">
                                    <RefreshCw className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
                                    <span className="text-slate-600 text-lg">Ürünler yükleniyor...</span>
                                </div>
                            </div>
                        ) : data?.products && data.products.length > 0 ? (
                            viewMode === 'table' ? (
                                <>
                                    {/* Desktop Table View */}
                                    <div className="hidden lg:block overflow-x-auto w-full">
                                        <table className="w-full table-auto min-w-[800px]">
                                            <thead>
                                                <tr className="border-b border-slate-200">
                                                    <th className="text-left py-3 px-2 font-semibold text-slate-900 text-xs sm:text-sm">ID</th>
                                                    <th className="text-left py-3 px-2 font-semibold text-slate-900 text-xs sm:text-sm">SKU</th>
                                                    <th className="text-left py-3 px-2 font-semibold text-slate-900 text-xs sm:text-sm">Ürün Adı</th>
                                                    <th className="text-left py-3 px-2 font-semibold text-slate-900 text-xs sm:text-sm hidden lg:table-cell">Ana Kategori</th>
                                                    <th className="text-left py-3 px-2 font-semibold text-slate-900 text-xs sm:text-sm hidden md:table-cell">Kategori</th>
                                                    <th className="text-left py-3 px-2 font-semibold text-slate-900 text-xs sm:text-sm hidden xl:table-cell">Alt Kategori</th>
                                                    <th className="text-left py-3 px-2 font-semibold text-slate-900 text-xs sm:text-sm">Fiyat</th>
                                                    <th className="text-left py-3 px-2 font-semibold text-slate-900 text-xs sm:text-sm hidden lg:table-cell">Para Birimi</th>
                                                    <th className="text-left py-3 px-2 font-semibold text-slate-900 text-xs sm:text-sm hidden xl:table-cell">KDV</th>
                                                    <th className="text-left py-3 px-2 font-semibold text-slate-900 text-xs sm:text-sm">Stok</th>
                                                    <th className="text-left py-3 px-2 font-semibold text-slate-900 text-xs sm:text-sm hidden md:table-cell">Marka</th>
                                                    <th className="text-left py-3 px-2 font-semibold text-slate-900 text-xs sm:text-sm">Resim</th>
                                                    <th className="text-left py-3 px-2 font-semibold text-slate-900 text-xs sm:text-sm">İşlemler</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {data.products.map((product, idx) => (
                                                    <tr key={product.id} className="border-b border-slate-100 hover:bg-slate-50/80 transition-colors duration-200">
                                                        <td className="py-3 px-2 text-slate-700 text-xs sm:text-sm">{(currentPage - 1) * pageSize + idx + 1}</td>
                                                        <td className="py-3 px-2">
                                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                                                                {product.sku}
                                                            </span>
                                                        </td>
                                                        <td className="py-3 px-2 text-xs sm:text-sm text-slate-900 max-w-xs truncate" title={product.name}>
                                                            {product.name}
                                                        </td>
                                                        <td className="py-3 px-2 text-xs text-slate-600 max-w-24 truncate hidden lg:table-cell" title={`${product.mainCategory || '-'} (${product.mainCategoryId || '-'})`}>
                                                            <div className="text-xs">
                                                                <div className="font-medium">{product.mainCategory || '-'}</div>
                                                                <div className="text-slate-500">ID: {product.mainCategoryId || '-'}</div>
                                                            </div>
                                                        </td>
                                                        <td className="py-3 px-2 text-xs text-slate-600 max-w-24 truncate hidden md:table-cell" title={`${product.category || '-'} (${product.categoryId || '-'})`}>
                                                            <div className="text-xs">
                                                                <div className="font-medium">{product.category || '-'}</div>
                                                                <div className="text-slate-500">ID: {product.categoryId || '-'}</div>
                                                            </div>
                                                        </td>
                                                        <td className="py-3 px-2 text-xs text-slate-600 max-w-24 truncate hidden xl:table-cell" title={`${product.subCategory || '-'} (${product.subCategoryId || '-'})`}>
                                                            <div className="text-xs">
                                                                <div className="font-medium">{product.subCategory || '-'}</div>
                                                                <div className="text-slate-500">ID: {product.subCategoryId || '-'}</div>
                                                            </div>
                                                        </td>
                                                        <td className="py-3 px-2 text-sm sm:text-lg font-semibold text-slate-900">₺{Number(product.price || 0).toFixed(2)}</td>
                                                        <td className="py-3 px-2 text-slate-700 text-xs sm:text-sm hidden lg:table-cell">{product.currency || 'TRL'}</td>
                                                        <td className="py-3 px-2 text-xs text-slate-600 hidden xl:table-cell">{product.tax || 0}%</td>
                                                        <td className="py-3 px-2">
                                                            <span className={`inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${product.stock > 10 ? 'bg-green-100 text-green-800 border border-green-200' :
                                                                product.stock > 0 ? 'bg-yellow-100 text-yellow-800 border border-yellow-200' :
                                                                    'bg-red-100 text-red-800 border border-red-200'
                                                                }`}>
                                                                {product.stock}
                                                            </span>
                                                        </td>
                                                        <td className="py-3 px-2 text-xs text-slate-600 max-w-20 truncate hidden md:table-cell" title={product.brand || '-'}>
                                                            {product.brand || '-'}
                                                        </td>
                                                        <td className="py-3 px-2">
                                                            <div
                                                                className="h-16 w-16 sm:h-20 sm:w-20 lg:h-32 lg:w-32 xl:h-52 xl:w-52 rounded-md overflow-hidden bg-slate-100 border border-slate-200 cursor-pointer hover:scale-105 transition-transform duration-200"
                                                                onClick={() => handleImageClick(product.image1 || extractImageUrl(product.description) || '/placeholder.png')}
                                                            >
                                                                <img src={product.image1 || extractImageUrl(product.description) || '/placeholder.png'} alt={product.name} className="h-full w-full object-cover" />
                                                            </div>
                                                        </td>
                                                        <td className="py-3 px-2">
                                                            <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-1 sm:space-y-0 sm:space-x-2 lg:space-x-3">
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline"
                                                                    onClick={() => handlePrintBarcode(product)}
                                                                    className="text-blue-600 border-blue-200 hover:bg-blue-50 hover:border-blue-300 transition-all duration-200 text-xs sm:text-sm px-2 sm:px-3"
                                                                >
                                                                    <Printer className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                                                                    <span className="hidden sm:inline">Barkod</span>
                                                                    <span className="sm:hidden">B</span>
                                                                </Button>
                                                                <TelegramButton
                                                                    productId={product.id}
                                                                    size="sm"
                                                                    variant="outline"
                                                                    className="text-green-600 border-green-200 hover:bg-green-50 hover:border-green-300 transition-all duration-200 text-xs sm:text-sm px-2 sm:px-3"
                                                                />
                                                                <Button
                                                                    size="sm"
                                                                    variant="ghost"
                                                                    className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-all duration-200 text-xs sm:text-sm px-2 sm:px-3"
                                                                >
                                                                    <Eye className="w-3 h-3 sm:w-4 sm:h-4" />
                                                                </Button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Mobile Accordion View */}
                                    <div className="lg:hidden space-y-3">
                                        {data.products.map((product, idx) => {
                                            const isExpanded = expandedProducts.has(product.id)
                                            return (
                                                <Card key={product.id} className="shadow-lg border-0 bg-white/80 backdrop-blur-sm">
                                                    <CardContent className="p-0">
                                                        {/* Accordion Header */}
                                                        <div
                                                            className="p-4 cursor-pointer hover:bg-slate-50/50 transition-colors duration-200"
                                                            onClick={() => toggleProductExpansion(product.id)}
                                                        >
                                                            <div className="flex items-center justify-between">
                                                                <div className="flex items-center space-x-3 flex-1 min-w-0">
                                                                    {/* Product Image */}
                                                                    <div
                                                                        className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden bg-slate-100 border border-slate-200 flex-shrink-0 cursor-pointer hover:scale-105 transition-transform duration-200"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation()
                                                                            handleImageClick(product.image1 || extractImageUrl(product.description) || '/placeholder.png')
                                                                        }}
                                                                    >
                                                                        <img
                                                                            src={product.image1 || extractImageUrl(product.description) || '/placeholder.png'}
                                                                            alt={product.name}
                                                                            className="w-full h-full object-cover"
                                                                        />
                                                                    </div>

                                                                    {/* Product Info */}
                                                                    <div className="flex-1 min-w-0">
                                                                        <div className="flex items-center space-x-2 mb-1">
                                                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                                                                                {product.sku}
                                                                            </span>
                                                                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${product.stock > 10 ? 'bg-green-100 text-green-800' :
                                                                                product.stock > 0 ? 'bg-yellow-100 text-yellow-800' :
                                                                                    'bg-red-100 text-red-800'
                                                                                }`}>
                                                                                {product.stock}
                                                                            </span>
                                                                        </div>
                                                                        <h3 className="font-semibold text-sm text-slate-900 line-clamp-2 mb-1">
                                                                            {product.name}
                                                                        </h3>
                                                                        <div className="text-lg font-bold text-slate-900">
                                                                            ₺{Number(product.price || 0).toFixed(2)}
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {/* Expand/Collapse Icon */}
                                                                <div className="flex-shrink-0 ml-2">
                                                                    {isExpanded ? (
                                                                        <ChevronUp className="w-5 h-5 text-slate-500" />
                                                                    ) : (
                                                                        <ChevronDown className="w-5 h-5 text-slate-500" />
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Accordion Content */}
                                                        {isExpanded && (
                                                            <div className="border-t border-slate-200 p-4 bg-slate-50/50">
                                                                <div className="space-y-4">
                                                                    {/* Product Details */}
                                                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                                                        <div>
                                                                            <span className="font-medium text-slate-700">Ana Kategori:</span>
                                                                            <p className="text-slate-600">{product.mainCategory || '-'}</p>
                                                                        </div>
                                                                        <div>
                                                                            <span className="font-medium text-slate-700">Kategori:</span>
                                                                            <p className="text-slate-600">{product.category || '-'}</p>
                                                                        </div>
                                                                        <div>
                                                                            <span className="font-medium text-slate-700">Alt Kategori:</span>
                                                                            <p className="text-slate-600">{product.subCategory || '-'}</p>
                                                                        </div>
                                                                        <div>
                                                                            <span className="font-medium text-slate-700">Marka:</span>
                                                                            <p className="text-slate-600">{product.brand || '-'}</p>
                                                                        </div>
                                                                        <div>
                                                                            <span className="font-medium text-slate-700">Para Birimi:</span>
                                                                            <p className="text-slate-600">{product.currency || 'TRL'}</p>
                                                                        </div>
                                                                        <div>
                                                                            <span className="font-medium text-slate-700">KDV:</span>
                                                                            <p className="text-slate-600">{product.tax || 0}%</p>
                                                                        </div>
                                                                    </div>

                                                                    {/* Action Buttons */}
                                                                    <div className="flex flex-col sm:flex-row gap-2 pt-4 border-t border-slate-200">
                                                                        <Button
                                                                            size="sm"
                                                                            variant="outline"
                                                                            onClick={() => handlePrintBarcode(product)}
                                                                            className="flex-1 text-blue-600 border-blue-200 hover:bg-blue-50 text-sm"
                                                                        >
                                                                            <Printer className="w-4 h-4 mr-2" />
                                                                            Barkod Yazdır
                                                                        </Button>
                                                                        <TelegramButton
                                                                            productId={product.id}
                                                                            size="sm"
                                                                            variant="outline"
                                                                            className="flex-1 text-green-600 border-green-200 hover:bg-green-50 text-sm"
                                                                        />
                                                                        <Button
                                                                            size="sm"
                                                                            variant="ghost"
                                                                            className="flex-1 text-slate-600 hover:text-slate-900 hover:bg-slate-100 text-sm"
                                                                        >
                                                                            <Eye className="w-4 h-4 mr-2" />
                                                                            Detay
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </CardContent>
                                                </Card>
                                            )
                                        })}
                                    </div>
                                </>
                            ) : (
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                                    {data.products.map((product) => (
                                        <Card key={product.id} className="shadow-lg hover:shadow-xl transition-all duration-300 border-0 bg-white/60 backdrop-blur-sm hover:bg-white/80">
                                            <CardContent className="p-4 sm:p-6">
                                                <div className="space-y-3 sm:space-y-4">
                                                    <div>
                                                        <h3 className="font-semibold text-sm sm:text-lg text-slate-900 mb-2 line-clamp-2">{product.name}</h3>
                                                        <div
                                                            className="h-32 sm:h-48 lg:h-64 w-full rounded-md overflow-hidden bg-slate-100 border border-slate-200 mb-3 cursor-pointer hover:scale-105 transition-transform duration-200"
                                                            onClick={() => handleImageClick(product.image1 || extractImageUrl(product.description) || '/placeholder.png')}
                                                        >
                                                            <img src={product.image1 || extractImageUrl(product.description) || '/placeholder.png'} alt={product.name} className="h-full w-full object-cover" />
                                                        </div>
                                                        <p className="text-xs sm:text-sm text-slate-600 line-clamp-2">{product.sku}</p>
                                                    </div>

                                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                            {product.sku}
                                                        </span>
                                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${product.stock > 10 ? 'bg-green-100 text-green-800' :
                                                            product.stock > 0 ? 'bg-yellow-100 text-yellow-800' :
                                                                'bg-red-100 text-red-800'
                                                            }`}>
                                                            Stok: {product.stock}
                                                        </span>
                                                    </div>

                                                    <div className="text-sm sm:text-lg font-bold text-slate-900">
                                                        ₺{Number(product.price || 0).toFixed(2)}
                                                    </div>

                                                    <div className="flex flex-col sm:flex-row gap-2">
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => handlePrintBarcode(product)}
                                                            className="flex-1 text-blue-600 border-blue-200 hover:bg-blue-50 text-xs sm:text-sm"
                                                        >
                                                            <Printer className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                                                            <span className="hidden sm:inline">Barkod</span>
                                                            <span className="sm:hidden">B</span>
                                                        </Button>
                                                        <TelegramButton
                                                            productId={product.id}
                                                            size="sm"
                                                            variant="outline"
                                                            className="text-green-600 border-green-200 hover:bg-green-50 text-xs sm:text-sm"
                                                        />
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </div>
                            )
                        ) : (
                            <div className="text-center py-16">
                                <Package className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                                <p className="text-slate-600 text-lg">Ürün bulunamadı</p>
                            </div>
                        )}

                        {/* Sayfalama */}
                        {data && data.pagination?.totalPages > 1 && (
                            <div className="flex flex-col sm:flex-row items-center justify-between mt-6 sm:mt-8 pt-6 sm:pt-8 border-t border-slate-200 gap-4">
                                <div className="text-xs sm:text-sm text-slate-700">
                                    <span className="font-medium">
                                        Sayfa {currentPage} / {data.pagination.totalPages}
                                    </span>
                                </div>
                                <div className="flex items-center space-x-2 sm:space-x-3">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage === 1}
                                        className="border-slate-200 hover:bg-slate-50 disabled:opacity-50 text-xs sm:text-sm px-3 sm:px-4"
                                    >
                                        Önceki
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage === data.pagination.totalPages}
                                        className="border-slate-200 hover:bg-slate-50 disabled:opacity-50 text-xs sm:text-sm px-3 sm:px-4"
                                    >
                                        Sonraki
                                    </Button>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Barkod Yazdırma Modal */}
            {
                selectedProduct && (
                    <BarcodePrintModal
                        product={selectedProduct}
                        isOpen={isModalOpen}
                        onClose={() => {
                            setIsModalOpen(false)
                            setSelectedProduct(null)
                        }}
                    />
                )
            }

            {/* Tam Senkronizasyon Modal */}
            <SyncModal
                isOpen={isSyncModalOpen}
                onClose={() => setIsSyncModalOpen(false)}
                accessToken={accessToken || ''}
            />

            {/* Resim Modal */}
            {
                isImageModalOpen && selectedImage && (
                    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                        <div className="relative max-w-4xl max-h-[90vh] bg-white rounded-lg overflow-hidden">
                            <div className="absolute top-4 right-4 z-10">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setIsImageModalOpen(false)}
                                    className="bg-white/90 hover:bg-white border-slate-200"
                                >
                                    ✕
                                </Button>
                            </div>
                            <img
                                src={selectedImage}
                                alt="Ürün Görseli"
                                className="w-full h-full object-contain max-h-[90vh]"
                            />
                        </div>
                    </div>
                )
            }
        </div >
    )
}
